import { useState } from 'react'
import { BadgeCheckIcon, ChevronRightIcon, GlobeIcon } from 'lucide-react'
import Price from '@/components/money/price'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { useUnitDiscountQuery } from '@/api/accommodation/hooks'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useIsMobile'
import { SITE_LABELS, SITE_LOGOS } from '@/lib/rating'
import { trackPriceGuaranteeOpened } from '@/lib/track'
import { buildComparison, getSitesWithPrice, guaranteeSubtitle } from './comparison'
import { PriceGuaranteeSheet } from './PriceGuaranteeSheet'

interface PriceGuaranteePanelProps {
  accommodation: AccommodationDetail
  defaultOpen?: boolean
}

type PriceRow = {
  id: string
  label: string
  logo?: string
  price: number
  isMatevolno?: boolean
}

export function PriceGuaranteePanel({ accommodation, defaultOpen = false }: PriceGuaranteePanelProps) {
  const [open, setOpen] = useState(defaultOpen)
  const isMobile = useIsMobile()
  const { discount } = useUnitDiscountQuery(accommodation.unit.id)

  if (!discount) {
    return null
  }

  const sitesWithPrice = getSitesWithPrice(accommodation)
  const comparison = buildComparison(discount, sitesWithPrice)

  // Our price is the single cheapest night in the availability calendar, while
  // averagePrice is a platform-wide average — so a platform can still come out
  // lower. Hide those rows; the user can report the better price via the form
  // instead. Tracking above still carries all prices.
  const displayedSites = sitesWithPrice.filter((site) => site.averagePrice >= discount.minPrice)
  const priceRows: PriceRow[] = [
    {
      id: 'matevolno',
      label: 'Mátevolno',
      logo: '/img/logo_matevolno.svg',
      price: discount.minPrice,
      isMatevolno: true,
    },
    ...displayedSites.map((site) => ({
      id: site.site,
      label: SITE_LABELS[site.site],
      logo: SITE_LOGOS[site.site],
      price: site.averagePrice,
    })),
    {
      id: 'direct',
      label: 'Web ubytování',
      price: discount.maxPrice,
    },
  ].sort((a, b) => a.price - b.price)

  const openPanel = () => {
    trackPriceGuaranteeOpened(accommodation, comparison)
    setOpen(true)
  }

  const title = 'Garance nejlepší ceny'
  const description = 'Porovnejte naši nejnižší cenu za noc s ostatními weby. Ceny ověřujeme každý den.'

  const trigger = (
    <button
      type="button"
      onClick={openPanel}
      className="flex w-full items-center gap-2.5 rounded-xl border border-green-200 bg-white px-3 py-2.5 text-left transition-colors hover:bg-green-100/40"
    >
      <BadgeCheckIcon className="size-5 shrink-0 text-green-600" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-green-700">{title}</div>
        <div className="truncate text-xs text-slate-500">{guaranteeSubtitle(comparison.isCheapest)}</div>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-green-700" />
    </button>
  )

  const content = (
    <>
      <div className="divide-y divide-slate-100">
        {priceRows.map((row) => (
          <div key={row.id} className="flex items-center justify-between gap-3 py-3">
            {row.isMatevolno ? (
              <div className="flex min-w-0 flex-col items-start gap-1">
                <div className="relative h-5 w-24 shrink-0 overflow-hidden">
                  <img
                    src={row.logo}
                    alt={row.label}
                    className="absolute -top-[19px] -left-[38px] h-[66px] w-auto max-w-none"
                  />
                </div>
                <span className="text-[11px] leading-none font-medium text-slate-500">Naše nejnižší cena</span>
              </div>
            ) : row.logo ? (
              <img
                src={row.logo}
                alt={row.label}
                className="h-5 w-auto max-w-24 shrink-0 object-contain"
                loading="lazy"
              />
            ) : (
              <div className="flex min-w-0 items-center gap-2 text-slate-700">
                <GlobeIcon className="size-5 shrink-0 text-slate-500" />
                <span className="truncate text-sm">{row.label}</span>
              </div>
            )}
            <span className="text-sm font-semibold whitespace-nowrap text-slate-900">
              {row.isMatevolno && <span className="text-red">od </span>}
              <Price price={row.price} className={row.isMatevolno ? 'text-red' : undefined} />{' '}
              <span className="font-normal text-slate-400">/ noc</span>
            </span>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-1.5 rounded-xl bg-green-50 px-4 py-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-green-700">
          <BadgeCheckIcon className="size-3.5 shrink-0" />
          Ceny ověřujeme každý den.
        </div>
        <PriceGuaranteeSheet
          accommodation={accommodation}
          renderTrigger={(openSheet) => (
            <button
              type="button"
              onClick={openSheet}
              className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700"
            >
              Našli jste lepší cenu? Napište nám.
            </button>
          )}
        />
      </div>
    </>
  )

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="max-h-[90dvh]">
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
              <DrawerDescription>{description}</DrawerDescription>
            </DrawerHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">{content}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <>
      {trigger}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
          <SheetHeader className="border-b border-slate-100 p-6">
            <SheetTitle className="text-xl">{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">{content}</div>
        </SheetContent>
      </Sheet>
    </>
  )
}
